import { createPortal } from "react-dom";
import { BlackCross, CrossSvg } from "../../../assets";
import { useTheme } from "../../../context/ThemeProvider";
import { useLeaveSpectraMutation, useSpectraById } from "../../../api/queryHooks";
import ResponseLoader from "../../Loader/ResponseLoader";
import { getInitials } from "../../../utils/helpers";
import useSpectraDetailsModalStore from "../../../store/useSpectraDetailsModal";
import UserAuthStore from "../../../store/userStore";
import useSessionIdStore from "../../../store/useSessionIdStore";
import { PERMISSIONS } from "../../../utils/permissions";
import { ThirdButton } from "../../Button/Buttons";
import { useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";

const DetailsContent = ({ space }) => {
  const webSearchEnabled = space?.web_search_enabled !== false;
  return (
    <div className="max-h-[332px] bg-surface-inset opacity-100 overflow-y-auto show-scrollbar flex-grow pt-4 px-6">
      {/* Title and Emoji Section */}
      <div className="flex items-center gap-3 mb-6">
        <div className="photon-avatar text-sm font-semibold w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0 mb-[3px]">
          {space?.emoji ? (
            <span className="text-xl">{space?.emoji.emoji}</span>
          ) : (
            <span className="mt-[2px]">
              {getInitials(space.title)?.toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-grow">
          <h3 className="text-[13px] font-medium text-ink-ink dark:text-ink-white mb-1">
            Title
          </h3>
          <p className="text-ink-ink text-[13px] dark:text-ink-white font-medium">
            {space?.title || "Untitled Spectra"}
          </p>
        </div>
        <div className="text-left">
          <h3 className="text-[13px] font-medium text-ink-ink dark:text-ink-white mb-1">
            AI Model
          </h3>
          <div className="inline-flex items-center gap-1.5">
            <span
              title={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
              aria-label={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
              className="inline-flex items-center"
            >
              <Globe
                className={`w-3.5 h-3.5 ${
                  webSearchEnabled
                    ? "text-ink-primary dark:text-ink-accent"
                    : "text-ink-control-border dark:text-ink-gray-400 opacity-60"
                }`}
              />
            </span>
            <span
              className="photon-metadata-label text-[10px] px-[8px] py-[5px] font-semibold rounded-2xl opacity-100 border"
            >
              {space?.model_name || "No Model Selected"}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-[13px] font-medium text-ink-ink dark:text-ink-control-border mb-3">
          Description
        </h3>
        <p className="text-ink-hex-666666 text-[13px] dark:text-ink-white leading-relaxed">
          {space?.description || "No description provided."}
        </p>
      </div>

      <div className="mb-6">
        <h3 className="text-[13px] font-medium text-ink-ink dark:text-ink-control-border mb-3">
          System Instructions
        </h3>
        <p className="text-ink-hex-666666 text-[13px] dark:text-ink-white leading-relaxed whitespace-pre-wrap break-words">
          {space?.system_prompt || "No system instructions provided."}
        </p>
      </div>
    </div>
  );
};

const Modal = ({ spaceToShow, onClose }) => {
  const { theme } = useTheme();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { isLoading, data: spaceResponse } = useSpectraById(spaceToShow.space_id, orgId, user?.uid);
  const space = spaceResponse?.spectra;
  const navigate = useNavigate();

  const { mutate: leaveSpectra, isPending: isLeaving } = useLeaveSpectraMutation({
    onSuccess: () => {
      onClose();
      navigate("/spectra");
    },
  });

  const handleLeaveClick = () => {
    leaveSpectra({
      sid: spaceToShow.space_id,
      org_id: orgId,
      uid: user.uid,
      session_id: useSessionIdStore.getState().session_id,
    });
  };

  const isOwner = space ? PERMISSIONS.IS_OWNER(space) : true;

  return (
    <div
      className="max-w-[520px] w-full bg-surface-dialog py-6 shadow-[0px_1px_4px_#00000029] rounded-2xl flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6">
        <h2 className="text-xl text-ink-ink dark:text-ink-white font-semibold">
          Spectra Details
        </h2>
        <button onClick={onClose}>
          {theme === "dark" ? <CrossSvg /> : <BlackCross />}
        </button>
      </div>

      <hr className="mt-4 dark:border-edge-hex-aaaaaa80" />

      {isLoading ? (
        <div className="min-h-[320px] flex items-center justify-center">
          <ResponseLoader theme={theme} />
        </div>
      ) : (
        <>
          <DetailsContent space={space} />
          <hr className="dark:border-edge-hex-aaaaaa80" />
          {!isOwner && (
            <div className="px-6 pt-4 flex justify-end">
              <ThirdButton
                onClick={handleLeaveClick}
                disabled={isLeaving}
                className="bg-button-danger-solid border-button-border text-button-on-primary border-[0.5px] dark:border-0"
              >
                {isLeaving ? "Leaving..." : "Leave Spectra"}
              </ThirdButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SpectraDetailsModal = () => {
  const isOpen = useSpectraDetailsModalStore((state) => state.isOpen);
  const setIsOpen = useSpectraDetailsModalStore((state) => state.setIsOpen);
  const spaceToShow = useSpectraDetailsModalStore((state) => state.spaceToShow);

  const handleOutsideClick = (e) => {
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (isOpen)
    return createPortal(
      <div
        className="absolute z-[1000] top-0 left-0 w-screen px-4 h-[100dvh] bg-fill-black/50 flex items-center justify-center"
        onClick={handleOutsideClick}
      >
        <Modal spaceToShow={spaceToShow} onClose={handleClose} />
      </div>,
      document.body
    );
};

export default SpectraDetailsModal;
